import { type UserId } from '@terros/common'
import { createContext, useContext, useMemo } from 'react'
import { TokenManager } from './TokenManager'

export type Auth = {
  getToken: () => Promise<string | undefined>
  isAuthenticated: boolean
}

export type Stage = 'prod' | 'staging' | string

export type BackendContextType = {
  getStage: () => Stage
  setStage: (stage: Stage) => void
  getImpersonating: () => UserId | undefined
  setImpersonating: (userId?: UserId) => void
  setAuth: (auth: Auth) => void
  getToken: () => Promise<string | undefined>
  isAuthenticated: () => boolean
}

export const tokenManager = new TokenManager()

export const BackendContext = createContext<BackendContextType | undefined>(undefined)

export function useBackend(impersonateOverride?: UserId): BackendContextType {
  const context = useContext(BackendContext)

  const state = useMemo(
    () =>
      context && {
        ...context,
        getImpersonating: impersonateOverride ? () => impersonateOverride : context.getImpersonating,
        getToken: () => tokenManager.getToken(context.getToken),
      },
    [impersonateOverride, context]
  )

  if (!state) throw new Error('useBackend must be used within a BackendProvider')

  return state
}
